import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/components/ui/use-toast";
import { 
  getConversations, 
  getMessages, 
  sendMessage, 
  markMessagesAsRead,
  subscribeToMessages,
  cleanupAllSubscriptions,
  Conversation,
  Message,
  getOrCreateConversation,
  getConversationById
} from "@/services/chat";

export const useChat = (conversationId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(conversationId);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Refs para evitar re-renders desnecessários
  const loadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const conversationsLoadedRef = useRef(false);

  // Set initial conversation ID when provided
  useEffect(() => {
    if (conversationId && conversationId !== currentConversationId) {
      setCurrentConversationId(conversationId);
    }
  }, [conversationId]);

  // Load conversations com cache e debounce
  const loadConversations = useCallback(async () => {
    if (!user || loadingRef.current) return;
    
    // Debounce: evitar chamadas muito frequentes
    const now = Date.now();
    if (now - lastLoadTimeRef.current < 2000) {
      console.log("🚫 [useChat] Debounce: ignorando chamada muito frequente");
      return;
    }
    
    try {
      loadingRef.current = true;
      lastLoadTimeRef.current = now;
      setLoading(true);
      
      const data = await getConversations();
      
      // Merge com conversas existentes, preservando dados específicos
      setConversations(prev => {
        const merged = [...data];
        
        // Preservar dados de busca específica
        prev.forEach(existingConv => {
          const newIndex = merged.findIndex(c => c.id === existingConv.id);
          if (newIndex >= 0 && existingConv._loadedViaSpecificSearch) {
            merged[newIndex] = {
              ...merged[newIndex],
              ...existingConv,
              id: existingConv.id
            };
          }
        });
        
        return merged;
      });
      
      conversationsLoadedRef.current = true;
    } catch (error) {
      console.error("❌ [useChat] Error loading conversations:", error);
      toast({
        title: "Erro ao carregar conversas",
        description: "Não foi possível carregar suas conversas. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [user, toast]);

  // Load messages com cache
  const loadMessages = useCallback(async (force = false) => {
    if (!user || !currentConversationId) return;
    
    try {
      const data = await getMessages(currentConversationId);
      setMessages(data);
      
      // Mark messages as read
      await markMessagesAsRead(currentConversationId);
    } catch (error) {
      console.error("❌ [useChat] Error loading messages:", error);
    }
  }, [user, currentConversationId]);

  // Send a message - otimizado
  const handleSendMessage = useCallback(async (
    message: string, 
    recipientId: string
  ) => {
    if (!user || !currentConversationId || !message.trim() || sendingMessage) return;
    
    try {
      setSendingMessage(true);
      
      // Otimistic update
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}`,
        conversation_id: currentConversationId,
        message: message.trim(),
        sender_profile_id: user.id,
        sent_at: new Date().toISOString(),
        is_read: false,
        is_sender: true
      };
      
      setMessages(prev => [...prev, optimisticMessage]);
      
      const newMessage = await sendMessage(
        currentConversationId,
        message,
        recipientId
      );
      
      // Substituir mensagem otimística pela real
      setMessages(prev => 
        prev.map(msg => 
          msg.id === optimisticMessage.id ? newMessage : msg
        )
      );
      
    } catch (error) {
      console.error("❌ [useChat] Error sending message:", error);
      
      // Remover mensagem otimística em caso de erro
      setMessages(prev => 
        prev.filter(msg => !msg.id.startsWith('temp-'))
      );
      
      toast({
        title: "Erro ao enviar mensagem",
        description: "Não foi possível enviar sua mensagem. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setSendingMessage(false);
    }
  }, [user, currentConversationId, sendingMessage, toast]);

  // Start conversation - otimizado
  const startConversation = useCallback(async (mentorId: string) => {
    if (!user) return;
    
    try {
      setLoading(true);
      const conversationId = await getOrCreateConversation(mentorId);
      setCurrentConversationId(conversationId);
      
      // Reload conversations apenas se necessário
      if (conversationsLoadedRef.current) {
        await loadConversations();
      }
      
      return conversationId;
    } catch (error) {
      console.error("❌ [useChat] Error starting conversation:", error);
      toast({
        title: "Erro ao iniciar conversa",
        description: "Não foi possível iniciar a conversa. Tente novamente.",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, toast, loadConversations]);

  // Select a conversation
  const selectConversation = useCallback((id: string | undefined) => {
    setCurrentConversationId(id);
  }, []);

  // Force reload messages
  const forceReloadMessages = useCallback(() => {
    if (currentConversationId) {
      loadMessages(true);
    }
  }, [currentConversationId, loadMessages]);

  // Load specific conversation - otimizado
  const loadSpecificConversation = useCallback(async (conversationId: string) => {
    if (!user) return null;
    
    try {
      const conversation = await getConversationById(conversationId);
      
      if (conversation) {
        // Marcar como carregado via busca específica
        conversation._loadedViaSpecificSearch = true;
        
        // Adicionar à lista se não existir
        setConversations(prev => {
          const exists = prev.find(c => c.id === conversationId);
          if (!exists) {
            return [conversation, ...prev];
          } else {
            // Atualizar conversa existente
            return prev.map(c => 
              c.id === conversationId ? { ...c, ...conversation } : c
            );
          }
        });
        
        return conversation;
      }
      
      return null;
    } catch (error) {
      console.error("❌ [useChat] Error loading specific conversation:", error);
      return null;
    }
  }, [user]);

  // Subscribe to new messages - otimizado
  useEffect(() => {
    if (!currentConversationId) return;
    
    const handleNewMessage = (newMessage: Message) => {
      setMessages(prev => {
        // Evitar duplicatas
        const exists = prev.some(msg => msg.id === newMessage.id);
        if (exists) return prev;
        
        // Adicionar nova mensagem
        const userId = user?.id;
        const messageWithSender = {
          ...newMessage,
          is_sender: newMessage.sender_profile_id === userId
        };
        return [...prev, messageWithSender];
      });
    };
    
    const unsubscribe = subscribeToMessages(currentConversationId, handleNewMessage);
    
    return unsubscribe;
  }, [currentConversationId, user]);

  // Load initial data - otimizado
  useEffect(() => {
    if (user && !conversationsLoadedRef.current) {
      loadConversations();
    }
  }, [user, loadConversations]);

  // Load messages when conversation changes
  useEffect(() => {
    if (currentConversationId) {
      loadMessages();
    } else {
      setMessages([]);
    }
  }, [currentConversationId, loadMessages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAllSubscriptions();
    };
  }, []);

  // Memoized values para evitar re-renders
  const memoizedValues = useMemo(() => ({
    conversations,
    messages,
    currentConversationId,
    loading,
    sendingMessage
  }), [conversations, messages, currentConversationId, loading, sendingMessage]);

  return {
    ...memoizedValues,
    sendMessage: handleSendMessage,
    selectConversation,
    loadConversations,
    forceReloadMessages,
    loadSpecificConversation,
    startConversation
  };
};
